import { useSyncExternalStore, useCallback, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { refreshToken } from '../api/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

type RefreshState = 'idle' | 'refreshing' | 'failed';

let refreshState: RefreshState = 'idle';
const listeners = new Set<() => void>();

function getRefreshState() {
  return refreshState;
}

function setRefreshState(state: RefreshState) {
  refreshState = state;
  listeners.forEach((l) => l());
}

function subscribeRefreshState(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const { user, accessToken, isTokenExpired, setAuth, clearAuth } = useAuthStore();
  const currentRefreshState = useSyncExternalStore(subscribeRefreshState, getRefreshState);

  const tokenExpired = isTokenExpired();
  const hasStoredUser = user !== null;
  const awaitingRefresh = tokenExpired && hasStoredUser && currentRefreshState !== 'failed';

  const doRefresh = useCallback(async () => {
    if (currentRefreshState !== 'idle') return;

    setRefreshState('refreshing');
    try {
      const result = await refreshToken();
      if (result && user) {
        setAuth(user, result.access_token, result.expires_at);
        setRefreshState('idle');
      } else {
        clearAuth();
        setRefreshState('failed');
      }
    } catch {
      clearAuth();
      setRefreshState('failed');
    }
  }, [currentRefreshState, user, setAuth, clearAuth]);

  useEffect(() => {
    if (tokenExpired && hasStoredUser && currentRefreshState === 'idle') {
      doRefresh();
    }
  }, [tokenExpired, hasStoredUser, currentRefreshState, doRefresh]);

  if (awaitingRefresh) {
    return null;
  }

  if (!accessToken || tokenExpired) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}
